/**
 * Новости — вёрстка записи как в legacy main_news.tpl (заголовок, дата курсивом,
 * текст, <hr> между записями), плюс постраничная навигация.
 *
 * Legacy отдавал все новости одной страницей. Пагинация — сознательное отличие
 * (см. docs/DIFFERENCES.md): их уже 18 и список растёт. Номер страницы живёт в URL
 * (?page=2), поэтому ссылку на страницу можно передать или обновить браузером.
 * Разбивку делает бекенд: GET /api/content/news?page=N.
 */

import { Fragment, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { Material } from '@/api/types'
import { useApi } from '@/lib/useApi'

/** Ответ GET /api/content/news (Handlers\User\Content::news). */
interface NewsPageData {
  items: Material[]
  page: number
  pages: number
  per_page: number
  total: number
}

export function NewsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1)

  const { data, loading, error } = useApi<NewsPageData>(`/api/content/news?page=${page}`)
  const news = data?.items ?? []
  const pages = data?.pages ?? 1

  // при смене страницы возвращаем к началу списка, иначе остаёшься в середине текста
  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [page])

  const goTo = (next: number): void => {
    setSearchParams(next <= 1 ? {} : { page: String(next) })
  }

  if (loading && news.length === 0) return <p>Загрузка…</p>
  if (error !== null) return <p style={{ color: 'red' }}>{error}</p>

  return (
    <div>
      {news.map((item, index) => (
        <Fragment key={item.id}>
          <h3>{item.title}</h3>
          <i>{item.date}</i>
          <br />
          <div dangerouslySetInnerHTML={{ __html: item.text }} />
          {index < news.length - 1 && <hr />}
        </Fragment>
      ))}

      {pages > 1 && <Pagination page={page} pages={pages} onSelect={goTo} />}
    </div>
  )
}

/** Постраничная навигация в стиле Bootstrap 3 (он уже подключён из legacy-стилей). */
function Pagination({
  page,
  pages,
  onSelect,
}: {
  page: number
  pages: number
  onSelect: (page: number) => void
}) {
  // показываем не больше 7 номеров: текущий по центру, края — многоточием
  const numbers: number[] = []
  const from = Math.max(1, Math.min(page - 3, pages - 6))
  const to = Math.min(pages, from + 6)
  for (let index = from; index <= to; index++) numbers.push(index)

  return (
    <nav style={{ textAlign: 'center' }}>
      <ul className="pagination">
        <li className={page === 1 ? 'disabled' : undefined}>
          <a
            href="#"
            aria-label="Предыдущая"
            onClick={(event) => {
              event.preventDefault()
              if (page > 1) onSelect(page - 1)
            }}
          >
            <span aria-hidden="true">&laquo;</span>
          </a>
        </li>

        {from > 1 && (
          <li className="disabled">
            <span>…</span>
          </li>
        )}

        {numbers.map((number) => (
          <li key={number} className={number === page ? 'active' : undefined}>
            <a
              href="#"
              onClick={(event) => {
                event.preventDefault()
                onSelect(number)
              }}
            >
              {number}
            </a>
          </li>
        ))}

        {to < pages && (
          <li className="disabled">
            <span>…</span>
          </li>
        )}

        <li className={page === pages ? 'disabled' : undefined}>
          <a
            href="#"
            aria-label="Следующая"
            onClick={(event) => {
              event.preventDefault()
              if (page < pages) onSelect(page + 1)
            }}
          >
            <span aria-hidden="true">&raquo;</span>
          </a>
        </li>
      </ul>
    </nav>
  )
}
